import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories, colorMap } from 'utils';
import aboutbg from './../../images/aboutus-bg.png';
import { Squares } from 'components';
import { BackIcon, NextIcon } from 'icons';

export const ProductDetails = () => {
  const { t } = useTranslation();
  const { type, title } = useParams<{ type?: string; title?: string }>();
  const [selectedColorImage, setSelectedColorImage] = useState<string | null>(
    null,
  );
  const [selectedColorcode, setSelectedColorcode] = useState<string | null>(
    null,
  );
  const [showColorSection, setShowColorSection] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const category = type ? categories[type] : undefined;
  const product = category?.find((item) => item.title === title);

  // Set the first color as default when the product loads
  useEffect(() => {
    if (
      product?.colors &&
      product.colors.length > 0 &&
      product.colors[0].imageLink
    ) {
      setSelectedColorImage(product.colors[0].imageLink);
      setSelectedColorcode(product.colors[0].color);
    }
  }, [product]);

  if (!product) {
    return (
      <div className="text-center text-white">
        {t('productDetails.notFound')}
      </div>
    );
  }

  const translatedProduct = {
    ...product,
    type: t(`section3.categories.${type}.name`),
    title: t(`section3.categories.${type}.${product.title}.title`),
    detail1: t(`section3.categories.${type}.${product.title}.detail1`),
    detail2: t(`section3.categories.${type}.${product.title}.detail2`),
    detail3: t(`section3.categories.${type}.${product.title}.detail3`),
    row1: t(`section3.categories.${type}.${product.title}.row1`),
    row1_2: t(`section3.categories.${type}.${product.title}.row1_2`),
    row1_3: product.row1_3
      ? t(`section3.categories.${type}.${product.title}.row1_3`)
      : undefined,
    row2: t(`section3.categories.${type}.${product.title}.row2`),
    row2_2: t(`section3.categories.${type}.${product.title}.row2_2`),
    row2_3: product.row2_3
      ? t(`section3.categories.${type}.${product.title}.row2_3`)
      : undefined,
    row3: t(`section3.categories.${type}.${product.title}.row3`),
    row3_2: t(`section3.categories.${type}.${product.title}.row3_2`),
    row3_3: product.row3_3
      ? t(`section3.categories.${type}.${product.title}.row3_3`)
      : undefined,
    row4: t(`section3.categories.${type}.${product.title}.row4`),
    row4_2: t(`section3.categories.${type}.${product.title}.row4_2`),
    row4_3: product.row4_3
      ? t(`section3.categories.${type}.${product.title}.row4_3`)
      : undefined,
    row5: product.row5
      ? t(`section3.categories.${type}.${product.title}.row5`)
      : undefined,
    row5_2: product.row5_2
      ? t(`section3.categories.${type}.${product.title}.row5_2`)
      : undefined,
    row5_3: product.row5_3
      ? t(`section3.categories.${type}.${product.title}.row5_3`)
      : undefined,
  };

  const renderTableRows = () => {
    const rows = [];
    for (let i = 1; i <= 5; i++) {
      const rowKey = `row${i}`;
      const col1 = (translatedProduct as any)[rowKey];
      if (!col1) continue;
      const col2 = (translatedProduct as any)[`${rowKey}_2`];
      const col3 = (translatedProduct as any)[`${rowKey}_3`];

      const cellClass = `border p-2 ${i === 1 ? 'font-bold' : ''}`;

      rows.push(
        <tr key={i}>
          <td className={cellClass}>{col1}</td>
          <td className={cellClass}>{col2}</td>
          {col3 && <td className={cellClass}>{col3}</td>}
        </tr>,
      );
    }
    return rows;
  };

  const renderColorButtons = () => {
    return (
      <div className="flex flex-wrap gap-2">
        {product.colors!.map((colorObj, index) => {
          // Extract the color code (e.g., "RAL 9003", "108101") from the color string
          const colorCodeMatch = colorObj.color.match(
            /RAL\s\d{4}|VSR\d{3}|SW203G|DB703|YW359F|January2|\d{6}|\d{2}-\d{4}/,
          );
          const colorCode = colorCodeMatch ? colorCodeMatch[0] : null;

          // Get the hex value from colorMap, default to #1E90AA if not found or empty
          const buttonColor =
            colorCode && colorMap[colorCode] ? colorMap[colorCode] : '#1E90AA';

          return (
            <button
              key={index}
              className="px-4 py-2 text-white rounded hover:brightness-90 transition-colors"
              style={{
                backgroundColor: buttonColor,
                height: isSmallScreen ? '30px' : '50px',
                width: isSmallScreen ? '30px' : '50px',
              }}
              onClick={() => {
                if (colorObj.imageLink) {
                  setSelectedColorImage(colorObj.imageLink);
                  setSelectedColorcode(colorObj.color);
                } else {
                  console.log(`No image link available for ${colorObj.color}`);
                }
              }}
            ></button>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <section
        className="px-smallPx md:px-mediumPx lg:px-largePx xl:px-[250px] pt-40 pb-20"
        style={{
          backgroundImage: `url(${aboutbg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'top',
        }}
      >
        {!showColorSection ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 bg-[#f7f7f7] text-black rounded-xl shadow-xl lg:h-[620px]">
            <div className="p-5 sm:p-8 lg:p-10 flex flex-col justify-between">
              <div>
                <p className="text-lg font-semibold">
                  {translatedProduct.type}
                </p>
                <h1 className="text-3xl font-bold mt-2 text-[#1E90AA]">
                  {translatedProduct.title}
                </h1>
                <p className="mt-4 text-md">{translatedProduct.detail1}</p>
                <p className="mt-4 text-md text-gray-500">
                  {translatedProduct.detail2}
                </p>
                {product.detail3 ? (
                  <p className="mt-4 text-md text-gray-500">
                    {translatedProduct.detail3}
                  </p>
                ) : (
                  <></>
                )}
              </div>
              {(type === 'Zip Screen' || type === 'External Blinds') && (
                <div className="flex items-center gap-2 mt-4">
                  <Squares />
                  <button
                    className="w-fit text-[#1E90AA]"
                    onClick={() => setShowColorSection(true)}
                  >
                    Check colors available
                  </button>
                  <NextIcon />
                </div>
              )}
            </div>
            <div className="flex flex-col items-center justify-center">
              <img
                src={product.src2}
                alt={translatedProduct.title}
                className="p-5"
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 bg-[#f7f7f7] text-black rounded-xl shadow-xl lg:h-[620px]">
            {/* Left Column: Product Image */}
            <div className="flex justify-start items-center pb-5 ">
              <img
                src={selectedColorImage || product.src}
                alt={`${translatedProduct.title} color preview`}
                className="rounded-tl-lg self-start"
              />
            </div>
            {/* Right Column: Color Buttons */}
            <div className="flex flex-col justify-between p-5 gap-4">
              <p className="mb-5 text-gray-500 font-[600] text-[18px] md:text-[24px]">
                Color code:{' '}
                <span className="font-[800] text-black">
                  {selectedColorcode}
                </span>
              </p>

              {renderColorButtons()}
              <div className="flex flex-col gap-3 mt-5 md:mt-0">
                {type !== 'Zip Screen' && (
                  <h1 className="font-[600]">{t('coatingTypes')}</h1>
                )}
                {type === 'Zip Screen' ? (
                  <p>{t('colorDisclaimer.zipScreen')}</p>
                ) : (
                  <p>{t('colorDisclaimer.default')}</p>
                )}
              </div>

              <div className="flex items-center gap-2 mt-5 md:mt-0">
                <BackIcon />
                <button
                  className="w-fit text-[#1E90AA]"
                  onClick={() => setShowColorSection(false)}
                >
                  {t('backToProduct')}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Technical Parameters Section */}
      <section className="px-smallPx md:px-mediumPx lg:px-largePx xl:px-[250px] pb-20">
        <h3 className="text-lg font-semibold">
          {t('productDetails.technicalParameters')}
        </h3>
        <div className="mt-5">
          <table className="w-full border-collapse">
            <tbody>{renderTableRows()}</tbody>
          </table>
        </div>
      </section>
    </>
  );
};

export default ProductDetails;
